import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, LineChart, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { Modal, Button } from "react-bootstrap";
import { PencilSquare, JournalText, Hourglass, ChevronDown, ChevronUp } from "react-bootstrap-icons";
import PieResultComponent from './PieResultComponent';
import QuickNoteModal from "./QuickNoteModal";
import QuickBesoinModal from "./QuickBesoinModal";
import { useNotes } from "../services/NotesContext";
import { useBesoins } from "../services/BesoinsContext";
import { useAuth } from "../services/AuthContext";
import { useTranslation } from 'react-i18next';
import '../css/Result.css';

const QuizResponse = ({ scanId, scoringValue, answers, createdAt, from, onOpenLinkedNotes }) => {
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasActiveSubscription } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuickNoteOpen, setIsQuickNoteOpen] = useState(false);
  const [isQuickBesoinOpen, setIsQuickBesoinOpen] = useState(false);
  const [selectedBesoin, setSelectedBesoin] = useState(null);
  const [isQuickBesoinReadOnly, setIsQuickBesoinReadOnly] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [quickNotePresetCategory, setQuickNotePresetCategory] = useState(0);
  const { getNotesByScanId } = useNotes();
  const { getBesoinsByScanId, addBesoin } = useBesoins();
  const linkedNotes = getNotesByScanId(scanId);
  const linkedBesoins = getBesoinsByScanId(scanId);
  const hasLinkedNotes = from === "history" && linkedNotes.length > 0;
  const hasLinkedBesoins = from === "history" && linkedBesoins.length > 0;

  const closeModal = () => setIsModalOpen(false);

  const handleDotClick = (index, score) => {
    setSelectedIndex(index);
    setSelectedScore(score);
    setIsModalOpen(true);
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setIsModalOpen(false);
    }
  };

  // ✅ Appelée quand une note est créée depuis QuickNoteModal
  const handleSaveNote = (savedNote) => {
    setIsQuickNoteOpen(false);
  };

  // ✅ Appelée quand un besoin est créé depuis QuickBesoinModal
  const handleSaveBesoin = async (besoin) => {
    await addBesoin(besoin);
    setIsQuickBesoinOpen(false);
  };

  const handleQuickNoteClick = () => {
    if (from === "history") {
      if (hasLinkedNotes) {
        if (onOpenLinkedNotes) onOpenLinkedNotes(scanId, linkedNotes);
      } else {
        setQuickNotePresetCategory(0);
        setIsQuickNoteOpen(true);
      }
    } else {
      setQuickNotePresetCategory(0);
      setIsQuickNoteOpen(true);
    }
  };

  const handleQuickBesoinClick = () => {
    if (from === "history" && hasLinkedBesoins) {
      setSelectedBesoin(linkedBesoins[0]);
      setIsQuickBesoinReadOnly(true);
      setIsQuickBesoinOpen(true);
    } else {
      setSelectedBesoin(null);
      setIsQuickBesoinReadOnly(false);
      setIsQuickBesoinOpen(true);
    }
  };

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleString('fr-FR', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    : '';

  const COLORS = ['#6F3F89', '#375A9C', '#029FB2', '#29A648', '#E0B614', '#DA5E20', '#C11D24'];

  const translationKeys = [
    { min: 'red.min', max: 'red.max' },
    { min: 'orange.min', max: 'orange.max' },
    { min: 'yellow.min', max: 'yellow.max' },
    { min: 'green.min', max: 'green.max' },
    { min: 'cyan.min', max: 'cyan.max' },
    { min: 'blue.min', max: 'blue.max' },
    { min: 'purple.min', max: 'purple.max' },
  ];

  useEffect(() => {
    if (!answers) navigate('/');
  }, [answers, navigate]);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen]);

  const [expanded, setExpanded] = useState(false);

  const results = answers
    ? translationKeys.map((key, index) => ({
        labelLeft: t(key.min),
        labelRight: t(key.max),
        value: answers[index],
        color: COLORS[index % COLORS.length]
      }))
    : [];

  const ArrowLeft = ({ color = "#8884d8" }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
         xmlns="http://www.w3.org/2000/svg">
      <polygon points="0,5 16,0 16,10" fill={color} />
    </svg>
  );

  const ArrowRight = ({ color = "#8884d8" }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
         xmlns="http://www.w3.org/2000/svg">
      <polygon points="0,0 16,5 0,10" fill={color} />
    </svg>
  );

  const CustomDot = ({ cx, cy, index, payload, stroke }) => {
    const handleClick = () => handleDotClick(index, payload.value);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={stroke}
        stroke={stroke}
        strokeWidth={12}
        className="smart-bump-dot"
        style={{ 
          cursor: "pointer",
          transformOrigin: `${cx}px ${cy}px`, // Nécessaire pour que le scale se fasse depuis le centre du point
          animationDelay: `${index * 0.15}s`  // Décalage pour l'effet en cascade ("l'un après l'autre")
        }}
        onClick={handleClick}
      />
    );
  };

  let modalContent = [];
  if (selectedIndex !== null && selectedScore !== null) {
    const type = selectedScore >= 6 ? "positif" : "negatif";
    modalContent = [1, 2, 3].map(i =>
      t(`scan.reflection.index${selectedIndex}.${type}${i}`)
    );
  }


  return (
    
    <div className={`result-container ${from === "history" ? "history-style" : "result-style"}`}>
      <div className="result-header">
        <span className="scan-date">{formattedDate}</span>
      </div>

      {/* --- Bouton Quick Note principal (droite) --- */}
      {/* --- Bouton Quick Note principal (droite) --- */}
      {((from === "history" && hasLinkedNotes) || (from !== "history" && hasActiveSubscription)) && (
        <div
          className={`${
            from === "history"
              ? "quick-note-history-container"
              : "quick-note-container"
          }`}
        >
          <Button
            variant="link"
            className={`quick-note-btn`}
            onClick={handleQuickNoteClick}
            style={{ position: "relative" }} // nécessaire pour positionner le "+"
          >
            <JournalText
              size={36}
              color={
                from === "history"
                  ? hasLinkedNotes
                    ? "#FFD166" // 🟡 notes existantes
                    : "#A3A3A3" // ⚪ aucune note
                  : "#A3A3A3" // 🟣 mode création
              }
            />

            {/* --- Petit + superposé UNIQUEMENT en mode création --- */}
            {(from !== "history") && (
              <span
                style={{
                  position: "absolute",
                  bottom: "6px",
                  right: "6px",
                  backgroundColor: "#A3A3A3",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  boxShadow: "0 0 3px rgba(0,0,0,0.5)",
                }}
              >
                +
              </span>
            )}
          </Button>
        </div>
      )}

      {/* --- Bouton Quick Besoin (gauche) --- */}
      {((from === "history" && hasLinkedBesoins) || (from !== "history" && hasActiveSubscription)) && (
        <div
          className={`${
            from === "history"
              ? "quick-besoin-history-container"
              : "quick-besoin-container"
          }`}
        >
          <Button
            variant="link"
            className="quick-note-btn"
            onClick={handleQuickBesoinClick}
            style={{ position: "relative" }}
          >
            <Hourglass
              size={36}
              color={
                from === "history"
                  ? hasLinkedBesoins
                    ? "#FFD166" // 🟡 besoins existants
                    : "#A3A3A3" // ⚪ aucun besoin
                  : "#A3A3A3"
              }
            />
            {/* --- Petit + superposé UNIQUEMENT en mode création --- */}
            {(from !== "history") && (
              <span
                style={{
                  position: "absolute",
                  bottom: "6px",
                  right: "6px",
                  backgroundColor: "#A3A3A3",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  boxShadow: "0 0 3px rgba(0,0,0,0.5)",
                }}
              >
                +
              </span>
            )}
          </Button>
        </div>
      )}

      <PieResultComponent
        currentQuestion={1}
        scoringValue={scoringValue}
        answers={answers}
        from={from}
        onSliceClick={handleDotClick}
      />

      {from === "history" && (
        <div
          onClick={() => setExpanded(!expanded)}
          style={{
            display: "flex",
            justifyContent: "center",
            cursor: "pointer",
            margin: "8px 0",
            color: "#f5f5f5",
          }}
        >
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      )}

      {(from !== "history" || expanded) && (
        <div className="results">
          {results.map((result, index) => (
            <div key={index} className="result-item">
              <div className="result-row">
                <span className="label-left">{result.labelLeft}</span>
                <div className="responsive-result-line">
                  <ResponsiveContainer width="100%" height={50}>
                    <LineChart data={[{ value: result.value }]} margin={{ top: 20, bottom: 0 }}>
                      <XAxis
                        type="number"
                        dataKey="value"
                        domain={[0, 11]}
                        stroke="#646266"
                        ticks={[]}
                        axisLine
                        tick={false}
                      />
                      <YAxis hide />
                      <Line
                        type="linear"
                        dataKey="value"
                        strokeWidth={12}
                        dot={(dotProps) => {
                          const { key, ...rest } = dotProps;
                          return (
                            <CustomDot
                              key={`dot-${index}-${dotProps.index}`}
                              {...rest}
                              index={index}
                              stroke={COLORS[COLORS.length - index - 1]}
                            />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="arrow-left"><ArrowLeft color="#646266" /></div>
                <div className="arrow-right"><ArrowRight color="#646266" /></div>
                <span className="label-right">{result.labelRight}</span>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* --- Graphiques --- */}
      {/* <PieResultComponent
        currentQuestion={1}
        scoringValue={scoringValue}
        answers={answers}
        from={from}
        onSliceClick={handleDotClick}
      />

      <div className="results">
        {results.map((result, index) => (
          <div key={index} className="result-item">
            <div className="result-row">
              <span className="label-left">{result.labelLeft}</span>
              <div className="responsive-result-line">
                <ResponsiveContainer width="100%" height={50}>
                  <LineChart data={[{ value: result.value }]} margin={{ top: 20, bottom: 0 }}>
                    <XAxis
                      type="number"
                      dataKey="value"
                      domain={[0, 11]}
                      stroke="#646266"
                      ticks={[]}
                      axisLine
                      tick={false}
                    />
                    <YAxis hide />
                    <Line
                      type="linear"
                      dataKey="value"
                      strokeWidth={12}
                      dot={(dotProps) => {
                        const { key, ...rest } = dotProps;
                        return (
                          <CustomDot
                            key={`dot-${index}-${dotProps.index}`}
                            {...rest}
                            index={index}
                            stroke={COLORS[COLORS.length - index - 1]}
                          />
                        );
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="arrow-left"><ArrowLeft color="#646266" /></div>
              <div className="arrow-right"><ArrowRight color="#646266" /></div>
              <span className="label-right">{result.labelRight}</span>
            </div>
          </div>
        ))}
      </div> */}

      {/* --- Modal i18n --- */}
      <Modal show={isModalOpen} onHide={closeModal} animation centered backdrop keyboard>
        <div ref={modalRef}>
          <Modal.Header>
            <Modal.Title>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: COLORS[COLORS.length - selectedIndex - 1],
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  marginRight: 8,
                }}
              />
              {t('scan.reflection.index.title')}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <ul>
              {modalContent.map((line, i) => (
                <li key={i} className="text-custom">{line}</li>
              ))}
            </ul>
          </Modal.Body>

          <Modal.Footer className="reflection-footer">
            {hasActiveSubscription && (
              <Button
                variant="link"
                className="quick-note-footer-btn"
                onClick={() => {
                  setQuickNotePresetCategory(selectedIndex + 1 ?? 0);
                  setIsQuickNoteOpen(true);
                }}
              >
                <PencilSquare size={40} color={COLORS[COLORS.length - selectedIndex - 1]} />
              </Button>
            )}
            <Button variant="secondary" onClick={closeModal}>
              {t('scan.reflection.close')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* --- Quick Note Modal --- */}
      <QuickNoteModal
        show={isQuickNoteOpen}
        onClose={() => setIsQuickNoteOpen(false)}
        onSave={handleSaveNote}
        presetCategory={quickNotePresetCategory}
        scanResultId={scanId}
      />

      {/* --- Quick Besoin Modal --- */}
      <QuickBesoinModal
        show={isQuickBesoinOpen}
        onClose={() => setIsQuickBesoinOpen(false)}
        onSave={handleSaveBesoin}
        scanResultId={scanId}
        existingBesoin={selectedBesoin}
        readOnly={isQuickBesoinReadOnly}
      />


    </div>
  );
};

export default QuizResponse;
